"use client";
import React, { RefObject, useEffect } from "react";
import { Xmark } from "iconoir-react";
import { CartItems } from "./CartItems";
import { useCart } from "@/cart";
import { getCartNumberOfItems } from "../selectors";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { OrganizationRole } from "@/organizations/utils";
import {
  CHECKOUT_DISABLED_REASON,
  CheckoutDisabledReason,
} from "@/checkout/constants";
import { Typography } from "@cappelendamm/design-system/react";
import { CartTotal } from "@/cart/components/CartTotal";
import { CartDisabled } from "@/cart/components/CartDisabled";
import { usePreorderSkus } from "@/cart/hooks/usePreorderSkus";
import { isItemDigital } from "@/products/selectors/isItemDigital";
import { VippsExpressButton } from "@/vipps/components/VippsExpressButton";
import { getVippsExpressEligibility } from "@/vipps/eligibility";

export type CartProps = {
  ref: RefObject<HTMLDialogElement | null>;
  vippsInNetsForUnitSale: boolean;
};

export function Cart({ ref, vippsInNetsForUnitSale }: CartProps) {
  const { closeCart, cart, cartOpen, isPending } = useCart();
  const pathname = usePathname();
  const hasItemsInCart = getCartNumberOfItems(cart) > 0;
  const preorderSkus = usePreorderSkus(cart);

  const hasDigitalItems =
    cart?.items.some((item) => isItemDigital(item)) ?? false;
  const hasPreorderItems = preorderSkus.size > 0;
  const hasConflictingPaymentConstraints = hasDigitalItems && hasPreorderItems;
  const { orgRole } = useAuth();
  // The button itself checks the PostHog flag and hides for signed-in users.
  const showVippsExpress = getVippsExpressEligibility(cart, {
    longHorizonPreorderSkus: preorderSkus,
  }).eligible;

  useEffect(() => {
    closeCart();
  }, [pathname, closeCart]);

  let disabledReason: CheckoutDisabledReason | undefined;

  if (orgRole === OrganizationRole.STUDENT) {
    disabledReason = CHECKOUT_DISABLED_REASON.ORGANIZATION_STUDENT;
  }

  const isCheckoutDisabled = !!disabledReason;
  return (
    <dialog
      className="fixed grid max-h-full min-h-full min-w-full translate-x-[100dvw] grid-rows-[auto_1fr_auto_auto] place-items-center gap-4 p-4 transition-all duration-500 open:right-0 open:translate-x-[calc(100dvw-100%)] sm:min-w-fit sm:pr-8 starting:open:translate-x-full"
      ref={ref}
      onClick={({ clientX, clientY }) => {
        if (ref.current && cartOpen) {
          const rect = ref.current.getBoundingClientRect();
          const isInDialog =
            rect.top <= clientY &&
            clientY <= rect.top + rect.height &&
            rect.left <= clientX &&
            clientX <= rect.left + rect.width;
          if (!isInDialog) closeCart();
        }
      }}
    >
      <Typography
        as="h2"
        variant="title-md"
        className="order-first font-normal"
      >
        Handlekurv
      </Typography>

      {isCheckoutDisabled && (
        <CartDisabled
          disabledReason={disabledReason!}
          className="order-4 sm:max-w-[24rem]"
        />
      )}
      {!isCheckoutDisabled && hasItemsInCart && pathname !== "/utsjekk" && (
        <>
          <CartTotal
            className="order-4"
            cart={cart}
            isLoading={isPending}
            vippsInNetsForUnitSale={vippsInNetsForUnitSale}
            vippsExpress={showVippsExpress ? <VippsExpressButton /> : undefined}
            hasConflictingPaymentConstraints={hasConflictingPaymentConstraints}
            freeShippingThreshold={
              cart?.freeShippingThreshold != null
                ? Number(cart.freeShippingThreshold)
                : undefined
            }
          />
          <hr className="order-3 col-span-full -mx-4 w-[calc(100%+(--spacing(4))*2)] border-stroke-default" />
        </>
      )}

      <CartItems className="order-2 col-span-full h-fit w-full self-start" />
      <button
        type="button"
        className="absolute top-4 right-4 order-last"
        onClick={closeCart}
      >
        <Xmark width={24} height={24} />
        <span className="sr-only">Lukk handlekurven</span>
      </button>
    </dialog>
  );
}
